import {
  IFieldFormGroup,
  IFieldProps,
} from './../../../../core/models/service-change-requests-form-fields.model';
import { IFormBuilderFieldsData } from './../../../../core/models/form-builder-fields-data.model';
import { IServiceChangeRequest } from './../../../../core/models/service-change-request.model';
import { IServiceTemplate } from './../../../../core/models/service-template/service-template.model';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { EServiceChangeRequestSection } from './../../../../core/models/service-change-request-sections.enum';
import {
  IFilterAndPaginationFieldsBase,
  IUiAlertContent,
  ToastService,
  ExtractHttpErrorResponseMessage,
  FilterFieldsToQueryParamsBuilder,
  IHttpSingleDataResponse,
  updateApiFilter,
} from '@irembo-andela/irembogov3-common';
import { IServiceLanguage } from './../../../../core/models/service-language.model';
import { ServiceChangeRequestService } from './../../../../core/services/service-change-request.service';
import { ServiceTemplateService } from './../../../../core/services/service-template.service';
import { BehaviorSubject, finalize, of, switchMap, tap } from 'rxjs';
import { IServiceCategory } from './../../../../core/models/service-category.model';
import { EServiceChangeRequestStatus } from './../../../../core/models/service-change-request-status.enum';

interface IFilterFields extends IFilterAndPaginationFieldsBase {
  name: string | null;
  keywords: string | null;
}

@Component({
  selector: 'irembogov-configuration-translation',
  templateUrl: './configuration-translation.component.html',
  styleUrls: ['./configuration-translation.component.scss'],
})
export class ConfigurationTranslationComponent implements OnInit, OnChanges {
  @Input() changeRequest!: IServiceChangeRequest;
  @Input() defaultData: any = {};
  @Input() inReviewMode = false;
  @Input() formFields!: IFormBuilderFieldsData;
  @Input() changeRequestJsonWorkflowData: Record<string, unknown> = {};
  @Input() isTemplateMode = false;
  @Input() template: IServiceTemplate | null = null;
  @Output() translationFormSubmitted: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  EServiceChangeRequestSection = EServiceChangeRequestSection;
  formFieldsJsonData: Record<string, unknown> = {};
  serviceLanguagesCollectionSize = 0;
  EServiceChangeRequestStatus = EServiceChangeRequestStatus;
  page = 0;
  pageSize = 10;
  isLoading = false;
  showConfigFormErrorMessage = false;
  _filterObject: BehaviorSubject<IFilterFields> =
    new BehaviorSubject<IFilterFields>({
      page: 0,
      size: 8,
      name: '',
      keywords: '',
    });
  formAlertContent: IUiAlertContent = {
    title: '',
    message: '',
    type: 'warning',
  };

  serviceLanguages: IServiceLanguage[] = [];
  isLoadingLanguages = false;
  activeLanguage!: IServiceLanguage;
  selectedLanguageData!: any;
  apiTranslations: { [key: string]: string } = {};

  constructor(
    private serviceChangeRequestService: ServiceChangeRequestService,
    private serviceTemplateService: ServiceTemplateService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this._filterObject
      .asObservable()
      .pipe(
        switchMap((filter: IFilterFields) => {
          const queryParams: string =
            FilterFieldsToQueryParamsBuilder<IFilterFields>(filter);
          this.getServiceLanguages(queryParams, filter.page ?? 0);
          return of();
        })
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.onChangeRequestChanges(changes);
    this.onDefaultDataChanges(changes);
    this.onFormFieldsChanges(changes);

    // If defaultData changes and we have an active language, refresh translations
    if (changes['defaultData']?.currentValue && this.activeLanguage) {
      this.fetchAndUpdateTranslations(this.activeLanguage);
    }
  }

  private onChangeRequestChanges(changes: SimpleChanges) {
    if (changes['changeRequest']?.currentValue) {
      this.changeRequest = changes['changeRequest'].currentValue;
      this.updateFormField(this.changeRequest.serviceName);
      this.updateFormField(this.changeRequest.serviceSummary);
      this.updateFormField(this.changeRequest.knowledgeBaseUrl);
      this.updateFormField(this.changeRequest.comment);
      this.updateFormFieldWithNextSteps(
        (this.changeRequestJsonWorkflowData['nextSteps'] as Record<
          string,
          string
        >[]) ?? []
      );

      this.addServiceCategoryNameToFieldsList(this.changeRequest.categoryId);
    }
  }

  updateFormFieldWithNextSteps(nextStes: Record<string, string>[]) {
    nextStes.forEach(element => {
      this.updateFormField(element['title']);
      this.updateFormField(element['description']);
    });
  }

  private onDefaultDataChanges(changes: SimpleChanges) {
    if (changes['defaultData']?.currentValue) {
      this.defaultData = changes['defaultData'].currentValue;

      // Only update selected language data if we don't have API translations yet
      if (
        !this.apiTranslations ||
        Object.keys(this.apiTranslations).length === 0
      ) {
        const selectedDataInitializer =
          this.defaultData?.translations !== undefined
            ? this.defaultData?.translations[this.activeLanguage?.locale]
            : this.formFieldsJsonData;
        this.selectedLanguageData =
          Object.keys(selectedDataInitializer ?? {}).length > 0
            ? selectedDataInitializer
            : this.formFieldsJsonData;
        this.updateSelectedTranslationsFormFields();
      }
    }
  }

  private onFormFieldsChanges(changes: SimpleChanges) {
    if (changes['formFields']?.currentValue) {
      this.formFields = changes['formFields'].currentValue;
      this.formFields?.fields?.forEach((field: any) => {
        field?.fieldGroup.forEach((formSection: any) => {
          this.updateFormField(formSection?.props?.label);
          formSection.fieldGroup.forEach((formBlock: any) => {
            this.updateFormField(formBlock?.props?.label);
            formBlock.fieldGroup.forEach((formInputs: IFieldFormGroup) => {
              this.addFieldProps(formInputs.props);
              if (formInputs.props?.options) {
                formInputs.props.options.forEach(option => {
                  this.updateFormField(option.label);
                  if (typeof option.value === 'string') {
                    this.updateFormField(option.value);
                  }
                });
              }
              // Handle repeater fields
              if (formInputs.fieldArray?.fieldGroup) {
                formInputs.fieldArray.fieldGroup.forEach(repeaterField => {
                  // Handle props and validations for repeater fields
                  this.addFieldProps(repeaterField.props);
                  if (repeaterField.validation?.messages) {
                    this.addValidations(
                      repeaterField,
                      repeaterField.validation.messages
                    );
                  }
                  // Handle options in repeater fields
                  if (repeaterField.props?.options) {
                    repeaterField.props.options.forEach(option => {
                      this.updateFormField(option.label);
                      if (typeof option.value === 'string') {
                        this.updateFormField(option.value);
                      }
                    });
                  }
                });
              }
              if (formInputs.props?.configs) {
                // Allow Translate the Location Widget
                formInputs.props.configs.forEach(option => {
                  this.updateFormField(option?.label);
                });
                formInputs.props.configs.forEach(option => {
                  this.updateFormField(option?.placeholder);
                });
              }
              // Allow Translate the section message with links Widget
              if (
                formInputs.props?.links &&
                Array.isArray(formInputs.props?.links)
              ) {
                formInputs.props.links.forEach(linkItem => {
                  this.updateFormField(
                    (<Record<string, unknown>>linkItem)?.['name'] as string
                  );
                });
              }
              if (formInputs?.fieldArray?.fieldGroup) {
                formInputs?.fieldArray?.fieldGroup?.forEach(option => {
                  // Add Validation for nested fields. e.g: Repeater fields
                  this.addFieldProps(option.props);
                  this.addValidations(option, option.validation.messages);
                });
              }

              this.addValidations(formInputs, formInputs.validation.messages);
            });
          });
        });
      });
      if (this.activeLanguage) {
        this.selectedLanguageData = {
          ...(this.defaultData?.translations?.[this.activeLanguage.locale] ??
            {}),
          ...(this.selectedLanguageData ?? {}),
        };
        this.updateSelectedTranslationsFormFields();
      }
    }
  }

  private addFieldProps(props: IFieldProps) {
    this.updateFormField(props.label);
    this.updateFormField(props.placeholder);
    this.updateFormField(props?.hint);
    this.updateFormField(props?.internalDescription);
    this.updateFormField(props?.tooltip);
    this.updateFormField(props?.title);
    this.updateFormField(props?.message);
    this.updateFormField(props?.extraMessage);
    this.updateFormField(props?.addText);
  }

  private addValidations(
    formInputs: IFieldFormGroup,
    messages: Record<string, string>
  ) {
    Object.keys(messages).forEach((messageKey: string) => {
      this.updateFormField(formInputs.validation.messages[messageKey]);
    });
  }

  private updateFormField(key: string | undefined | null) {
    if (key === '' || key === undefined || key === null) {
      return;
    } else {
      this.formFieldsJsonData[key] = '';
      if (
        this.activeLanguage &&
        this.selectedLanguageData &&
        !(key in this.selectedLanguageData)
      ) {
        this.selectedLanguageData[key] = this.apiTranslations[key] ?? '';
      }
    }
  }

  getServiceLanguages(queryString: string, page: number) {
    this.isLoadingLanguages = true;
    this.serviceChangeRequestService
      .getServiceLanguages(queryString)
      .pipe(
        finalize(() => (this.isLoadingLanguages = false)),
        // After getting languages, fetch translations for the first language
        tap(response => {
          if (response?.data?.content?.length > 0 && page === 0) {
            const firstLanguage = response.data.content[0];
            this.serviceChangeRequestService.languages.next(
              response.data.content
            );
            this.activeLanguage = firstLanguage;
            // Fetch translations for the first language
            this.fetchAndUpdateTranslations(firstLanguage);
          }
        })
      )
      .subscribe({
        next: response => {
          this.serviceLanguages =
            page > 0
              ? this.serviceLanguages?.concat(response?.data?.content)
              : response?.data?.content;
          this.serviceLanguagesCollectionSize =
            response.data.totalPages * response.data.size;
        },
        error: err => {
          const errorMessage = ExtractHttpErrorResponseMessage(
            err,
            'Error getting services'
          );
          this.toastService.show({ body: errorMessage, type: 'error' });
        },
      });
  }

  private fetchAndUpdateTranslations(language: IServiceLanguage) {
    this.isLoading = true;

    this.serviceChangeRequestService
      .getStaticTranslations(language.locale)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: translations => {
          this.apiTranslations = translations;

          const languageObject: any = {
            ...(this.defaultData?.translations
              ? this.defaultData?.translations[language?.locale]
              : this.formFieldsJsonData),
          };

          // Merge API translations with existing translations
          Object.keys(this.formFieldsJsonData).forEach(key => {
            if (key in this.apiTranslations) {
              languageObject[key] = this.apiTranslations[key];
            }
          });

          this.selectedLanguageData =
            Object.keys(languageObject).length > 0
              ? languageObject
              : this.formFieldsJsonData;

          this.updateSelectedTranslationsFormFields();
        },
        error: () => {
          this.toastService.show({
            body: 'Failed to fetch API translations',
            type: 'error',
          });
          // Fall back to default translations
          this.updateLanguageTranslationsWithoutApi(language);
        },
      });
  }

  updateLanguageTranslationsByLanguage(language: IServiceLanguage) {
    this.activeLanguage = language;
    this.fetchAndUpdateTranslations(language);
  }

  private updateLanguageTranslationsWithoutApi(language: IServiceLanguage) {
    let languageObject: any = {};
    languageObject = {
      ...(this.defaultData?.translations
        ? this.defaultData?.translations[language?.locale]
        : this.formFieldsJsonData),
    };
    this.selectedLanguageData =
      Object.keys(languageObject).length > 0
        ? languageObject
        : this.formFieldsJsonData;
    this.updateSelectedTranslationsFormFields();
    this.activeLanguage = language;
  }

  onTranslationsSubmitted(event: boolean): void {
    this.translationFormSubmitted.emit(event);
  }

  private updateSelectedTranslationsFormFields(): void {
    const formFieldsKeys = Object.keys(this.formFieldsJsonData);

    formFieldsKeys.forEach(key => {
      if (!(key in this.selectedLanguageData)) {
        this.selectedLanguageData[key] = this.apiTranslations[key] ?? '';
      }
    });
  }

  addServiceCategoryNameToFieldsList(categoryId: string): void {
    this.serviceChangeRequestService
      .getServiceCategoryById(categoryId ?? '')
      .subscribe({
        next: (response: IHttpSingleDataResponse<IServiceCategory>) => {
          this.updateFormField(response.data.name);
        },
      });
  }

  fetchMoreLanguages() {
    if (this.serviceLanguages.length >= this.serviceLanguagesCollectionSize)
      return;
    this.page++;
    updateApiFilter(this._filterObject, {
      page: this.page,
      size: this.pageSize,
    });
  }
}
